import React from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    Button, Text, Box, Stack, Icon, UnorderedList, ListItem, useColorModeValue,
} from "@chakra-ui/react";
import { MdWarningAmber } from "react-icons/md";

/**
 * Renders the consequences list box for immediate activation.
 * @returns {React.ReactElement}
 */
function ConsequencesBox() {
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    const itemColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="12px" borderRadius="8px" bg={boxBg} mb="16px">
            <Text fontSize="0.82rem" fontWeight="600" mb="4px" color={titleColor}>Conséquences :</Text>
            <UnorderedList color={itemColor} fontSize="0.8rem" m="0" pl="20px">
                <ListItem>L'ancienne version sera désactivée</ListItem>
                <ListItem>Tous les consentements utilisateurs existants seront invalidés</ListItem>
                <ListItem>Les utilisateurs devront re-consentir à la prochaine connexion</ListItem>
            </UnorderedList>
        </Box>
    );
}

/**
 * Renders the GDPR recommendation box advising a 30-day notice period.
 * @returns {React.ReactElement}
 */
function GdprRecommendationBox() {
    return (
        <Box p="12px" borderRadius="8px" bg="rgba(33,150,243,0.06)" border="1px solid" borderColor="rgba(33,150,243,0.15)">
            <Text color="rgba(100,181,246,0.95)" fontSize="0.8rem">
                <strong>Recommandation RGPD :</strong> pour les modifications substantielles (CGU, politique de
                confidentialité), il est recommandé de définir une date d'effet avec un préavis de 30 jours afin
                de laisser aux utilisateurs le temps de prendre connaissance des changements avant leur entrée en vigueur.
            </Text>
        </Box>
    );
}

/**
 * Renders the consequences list and GDPR recommendation box.
 * Informs the user about version activation side effects.
 * @returns {React.ReactElement}
 */
function ActivationWarningContent() {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    return (
        <>
            <Text color={textColor} fontSize="0.88rem" mb="16px">
                Aucune date d'effet n'a été définie. Cette version sera activée immédiatement
                et un email de notification sera envoyé à tous les utilisateurs concernés.
            </Text>
            <ConsequencesBox />
            <GdprRecommendationBox />
        </>
    );
}

/**
 * Confirmation dialog shown when activating a legal document version immediately.
 * Warns that the version will activate right away and notify all users.
 * @param {object} props
 * @param {boolean} props.open - Whether dialog is visible
 * @param {function} props.onConfirm - Called when user confirms submission
 * @param {function} props.onCancel - Called when user cancels
 * @returns {React.ReactElement}
 */
function ConfirmActivationDialog({ open, onConfirm, onCancel }) {
    return (
        <Modal isOpen={open} onClose={onCancel} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent border="1px solid" borderColor="rgba(255,152,0,0.3)">
                <ModalHeader>
                    <Stack direction="row" spacing="8px" align="center" color="#ff9800">
                        <Icon as={MdWarningAmber} /> <Text>Activation immédiate</Text>
                    </Stack>
                </ModalHeader>
                <ModalBody><ActivationWarningContent /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onCancel}>Annuler</Button>
                    <Button colorScheme="orange" onClick={onConfirm}>Confirmer et activer maintenant</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ConfirmActivationDialog;
